import { type FC } from "react";
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Switch,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import type { ApolloError } from "@apollo/client";
import { useSetPlatformStripeBillingModeMutation } from "@/admin/entities/platforms";
import {
  StripeBillingModeType,
  type PlatformShowQueryType,
} from "@/types/graphql/admin.generated";

interface MissingPermission {
  permission: string;
  resource: string;
  access: string | null;
}

interface PlatformStripeBillingCardProps {
  platformId: string;
  stripeBillingMode: NonNullable<
    PlatformShowQueryType["platform"]
  >["stripeBillingMode"];
}

function errorExtensions(error: ApolloError): Record<string, unknown> {
  return error.graphQLErrors[0]?.extensions ?? {};
}

export const PlatformStripeBillingCard: FC<PlatformStripeBillingCardProps> = ({
  platformId,
  stripeBillingMode,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const enabled = stripeBillingMode === StripeBillingModeType.StripeBillingType;

  const [setMode, { loading, error }] = useSetPlatformStripeBillingModeMutation(
    {
      onCompleted: (data) => {
        enqueueSnackbar(
          data.setPlatformStripeBillingMode.stripeBillingMode ===
            StripeBillingModeType.StripeBillingType
            ? "Stripe Billing включён"
            : "Stripe Billing выключен",
          { variant: "success" }
        );
      },
      onError: () => undefined,
    }
  );

  const extensions = error ? errorExtensions(error) : {};
  const code = extensions.code;
  const missing = Array.isArray(extensions.missingPermissions)
    ? (extensions.missingPermissions as MissingPermission[])
    : [];

  let errorText: string | null = null;
  if (error) {
    if (code === "STRIPE_KEY_MISSING_PERMISSIONS") {
      errorText =
        "Ключу Stripe не хватает прав. Режим не включён, добавьте права и повторите.";
    } else if (code === "STRIPE_BILLING_HAS_SUBSCRIPTIONS") {
      errorText = "Выключить нельзя: есть активные подписки в Stripe";
    } else if (code === "STRIPE_BILLING_HAS_PENDING_PAYMENTS") {
      const pending =
        typeof extensions.pendingPayments === "number"
          ? ` (${extensions.pendingPayments})`
          : "";
      errorText = `Выключить нельзя: есть незавершённые оформления подписок в Stripe${pending}. Они отменяются сами не позже суток, после этого повторите.`;
    } else if (code === "STRIPE_NOT_CONNECTED") {
      errorText = "Сначала подключите Stripe в настройках платежей платформы";
    } else {
      errorText = error.message;
    }
  }

  return (
    <Card>
      <CardHeader
        title="Подписки Stripe"
        action={
          enabled ? (
            <Chip size="small" color="success" label="Включено" />
          ) : (
            <Chip size="small" label="Выключено" />
          )
        }
      />
      <CardContent>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              disabled={loading}
              onChange={(_event, checked) => {
                void setMode({
                  variables: {
                    platformId,
                    mode: checked
                      ? StripeBillingModeType.StripeBillingType
                      : StripeBillingModeType.OttkitType,
                  },
                });
              }}
            />
          }
          label="Stripe ведёт подписки (Stripe Billing)"
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Для включения ключу Stripe нужны права на Products, Prices,
          Subscriptions, Invoices и Coupons (Write).
        </Typography>
        {errorText ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorText}
            {missing.length > 0 ? (
              <List dense disablePadding>
                {missing.map((item) => (
                  <ListItem key={item.permission} disableGutters>
                    <ListItemText
                      primary={
                        item.access
                          ? `${item.resource}: ${item.access}`
                          : item.resource
                      }
                      secondary={item.permission}
                    />
                  </ListItem>
                ))}
              </List>
            ) : null}
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
};
